import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganizationId, useSubjectLanguageVariants } from '@features/user';
import { Skeleton } from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';

import { declinedMessage } from '../declinedMessage';
import { useSendRequirementRequestMutation } from '../store/api';
import { useOpenRequirements } from '../store/useOpenRequirements';
import { usePatientRequirements } from '../store/usePatientRequirements';

import type { RemindOutcome } from './RequirementAlertBadge';
import { RequirementAlertBadge } from './RequirementAlertBadge';


/*
  Lazy on purpose. The recorder's bar mounts this component on every encounter
  and never opens the management popover; a static import would put the record
  manager's tree — its queries, the waive dialog, the delivery history — into
  that bundle for a surface that cannot reach it.
*/
const PatientRequirementsManager = lazy(() =>
  import('./PatientRequirementsRecordSection').then(module => ({
    default: module.PatientRequirementsManager,
  })),
);

export interface PatientRequirementAlertProps {
  patientId: string | undefined;
  /**
   * `management` is the patient page (GDN-3592), where this replaced the
   * record section and the popover carries every action that section had.
   * `glance` is the recorder's bar and is unchanged.
   */
  variant?: 'glance' | 'management';
  /** Only used to attribute analytics to the visit; not required to render. */
  encounterId?: string;
}

/**
 * The recording-window provider alert (GDN-3225, AC-15).
 *
 * Renders nothing when the gate is off, while loading, or when the patient owes
 * nothing — the context bar is dense, and an alert that is sometimes an empty
 * placeholder trains providers to ignore it.
 */
export const PatientRequirementAlert = ({
  patientId,
  encounterId,
  variant = 'glance',
}: PatientRequirementAlertProps) => {
  const isManagement = variant === 'management';
  const enabled = useFeatureGate(FeatureGates.PATIENT_REQUIREMENTS);
  const subject = useSubjectLanguageVariants();
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  const { patientRequirementsProviderAlert } = useEventTracking();

  const { items, hasUnnamedItems, isLoading } = useOpenRequirements(patientId, enabled);

  /*
    On the patient page the pill persists for ANY requirement, in any state —
    open, requested, waived, received or cancelled (GDN-3592 criteria 7 and 8).

    The record section used to be how staff reached a settled patient's
    delivery history and their waived items with actor and reason. With the
    section gone, an alert that vanished the moment nothing was OPEN would take
    that history with it: the patient would simply have no requirements surface
    at all. Only a patient with zero requirements hides it.

    Deliberately NOT changed for the recorder, where "nothing outstanding is
    not an empty alert, it is no alert" still holds (criterion 4). Skipped
    entirely there, so the recorder gains no query.
  */
  const {
    rows: allRows,
    isError: requirementsFailed,
    isLoading: requirementsLoading,
  } = usePatientRequirements(patientId ?? '', enabled && isManagement);
  /*
    A failed read is NOT an empty patient. The query supplies an empty rows
    array on error, so reading `length === 0` as "no requirements" hid the pill
    exactly when something had gone wrong — and with the page-body section gone
    the staff member could then neither see the failure, retry it, nor reach
    history and waived items. Three states, not two.
  */
  const hasAnyRequirement = allRows.length > 0;
  const [sendRequest, { isLoading: isReminding }] = useSendRequirementRequestMutation();
  const [remindOutcome, setRemindOutcome] = useState<RemindOutcome | undefined>();

  const shown =
    enabled &&
    !isLoading &&
    (isManagement
      ? // Still loading stays quiet rather than flashing an error pill that a
        // moment later becomes a normal one.
        !requirementsLoading && (hasAnyRequirement || requirementsFailed)
      : items.length > 0);

  // One shown event per patient, so a provider scrolling the encounter does not
  // inflate the count. Keyed on the patient rather than the mount because the
  // bar survives patient changes within a session.
  // The context bar is reused across patients within a session, so state that
  // describes ONE patient has to be cleared when the patient changes —
  // otherwise the next patient's checklist opens showing the previous
  // patient's "Sent" confirmation mid-visit.
  // Tracks which patient the bar is currently showing, so a reply that arrives
  // after a switch can tell it is stale. A ref rather than state because the
  // async continuation below needs the value at settle time, not at call time.
  const currentPatientRef = useRef(patientId);
  // Identifies the in-flight send by REQUEST, not by patient. Keying on the
  // patient alone means two overlapping sends for the same patient share one
  // marker, so whichever finishes first clears it and re-enables Remind while
  // the other is still going. A counter gives each send its own identity.
  const remindSeq = useRef(0);
  const pendingRemind = useRef<{ id: number; patientId: string } | null>(null);

  useEffect(() => {
    currentPatientRef.current = patientId;
    setRemindOutcome(undefined);
  }, [patientId]);

  const shownFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!shown || !patientId || shownFor.current === patientId) return;
    shownFor.current = patientId;
    patientRequirementsProviderAlert.opened({
      organization_id: organizationId ?? undefined,
      patient_id: patientId,
      encounter_id: encounterId,
      open_count: items.length,
    });
  }, [
    shown,
    patientId,
    encounterId,
    items.length,
    organizationId,
    patientRequirementsProviderAlert,
  ]);

  if (!shown || !patientId) return null;

  const handleRemind = () => {
    patientRequirementsProviderAlert.remindClicked({
      organization_id: organizationId ?? undefined,
      patient_id: patientId,
      encounter_id: encounterId,
      open_count: items.length,
    });

    // Capture who this send is for. Clearing state on patient change is not
    // enough on its own: a slow reply for Patient A can settle AFTER the switch
    // and write A's outcome into B's freshly cleared state, telling the
    // provider that the patient in front of them has been reminded.
    const sentFor = patientId;
    const requestId = ++remindSeq.current;
    pendingRemind.current = { id: requestId, patientId: sentFor };

    void (async () => {
      try {
        const outcome = await sendRequest({
          orgId: organizationId ?? '',
          patientId,
          // The contract calls these "channels to attempt" and answers with
          // accepted vs failed per channel, so it resolves what actually goes
          // out. Both are requested because a provider mid-visit is the wrong
          // person to be picking a channel. NOTE: the contract does not state
          // that per-patient sends filter by the patient's channel
          // preferences — AC-12 guarantees that for bulk launch only — so this
          // deliberately does not claim it does.
          sendRequestBody: { channels: ['sms', 'email'] },
        }).unwrap();

        // A 200 means the request was processed, not that a message went out.
        // With every channel rejected there is nothing for the patient to act
        // on, so telling the provider "Sent" would be the same mistake as
        // treating delivery as completion.
        // Two conditions, not one: the bar must still be on the patient this
        // was sent for, AND this must be the newest send — an older reply
        // settling last would otherwise overwrite a newer one's result.
        if (currentPatientRef.current !== sentFor) return;
        if (remindSeq.current !== requestId) return;
        setRemindOutcome(
          outcome.accepted_channels.length > 0 ? { kind: 'sent' } : { kind: 'failed' },
        );
      } catch (error) {
        if (currentPatientRef.current !== sentFor) return;
        if (remindSeq.current !== requestId) return;
        // A declined send is an answer, not a fault: the server is telling
        // staff something about this patient that only it knows, and the bare
        // catch this replaces threw that away and reported every 409 as a
        // generic failure (GDN-3558).
        const declined = declinedMessage(error);
        setRemindOutcome(declined ? { kind: 'declined', message: declined } : { kind: 'failed' });
      } finally {
        // Only the newest send may clear the marker. An older one settling
        // late must leave a newer request's spinner alone.
        if (pendingRemind.current?.id === requestId) {
          pendingRemind.current = null;
        }
      }
    })();
  };

  return (
    <RequirementAlertBadge
      // Remount per patient: the badge owns its popover anchor, and an open
      // checklist must not survive the patient switching underneath it.
      key={patientId}
      items={items}
      hasUnnamedItems={hasUnnamedItems}
      subject={subject}
      variant={variant}
      errored={isManagement && requirementsFailed}
      managementContent={
        isManagement ? (
          <Suspense fallback={<Skeleton variant="rounded" height={180} />}>
            <PatientRequirementsManager patientId={patientId} />
          </Suspense>
        ) : undefined
      }
      /*
        Withheld, not hidden, on the management variant. The whole-patient send
        exists because a provider mid-visit should not be picking between
        items; on the record the popover now offers that same one patient-level
        control (GDN-3607, GDN-3608), so a second button here would be a
        duplicate of it rather than a different reach. "Open record" is withheld
        for the plainer reason that this IS the record.
      */
      onRemind={organizationId && !isManagement ? handleRemind : undefined}
      isReminding={isReminding && pendingRemind.current?.patientId === patientId}
      remindOutcome={remindOutcome}
      onOpenPatient={
        isManagement
          ? undefined
          : () => {
              patientRequirementsProviderAlert.openRecordClicked({
                organization_id: organizationId ?? undefined,
                patient_id: patientId,
                encounter_id: encounterId,
              });
              navigate(`/patient/${patientId}`);
            }
      }
      onOpen={() =>
        patientRequirementsProviderAlert.checklistOpened({
          organization_id: organizationId ?? undefined,
          patient_id: patientId,
          encounter_id: encounterId,
          open_count: items.length,
        })
      }
    />
  );
};
